/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import * as React from 'react'
import clsx from 'clsx'
import { capitalize, ownerDocument, useForkRef } from '@applift/factor'
import { useGridRootProps } from '../../hooks/utils/useGridRootProps'
import { ReactTableCell } from '../../models/gridReactTable'
import { flexRender } from '../../hooks/core/useReactTable'
import { GridCellEventLookup, GridEvents } from '../../models'
import { useGridApiContext } from '../../hooks/utils/useGridApiContext'
import { FocusElement } from '../../models/params/gridCellParams'
import { GridCellModes, GridEditCellProps } from '../../models/gridEditRowModel'
import styles from '../../style'

// Based on https://stackoverflow.com/a/59518678
let cachedSupportsPreventScroll: boolean
function doesSupportPreventScroll(): boolean {
  if (cachedSupportsPreventScroll === undefined) {
    document.createElement('div').focus({
      get preventScroll() {
        cachedSupportsPreventScroll = true
        return false
      },
    })
  }
  return cachedSupportsPreventScroll
}

export interface GridCellProps
  extends ReactTableCell,
    Omit<React.HTMLAttributes<HTMLDivElement>, 'id'> {
  height: number | 'auto'
  hasFocus?: boolean
  editCellState?: GridEditCellProps | null
  rowId: string
  showRightBorder?: boolean
  colIndex: number
  className: string
  disableDragEvents?: boolean
  onClick?: React.MouseEventHandler<HTMLDivElement>
  onMouseDown?: React.MouseEventHandler<HTMLDivElement>
  onMouseUp?: React.MouseEventHandler<HTMLDivElement>
  onDoubleClick?: React.MouseEventHandler<HTMLDivElement>
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>
  onDragEnter?: React.DragEventHandler<HTMLDivElement>
  onDragOver?: React.DragEventHandler<HTMLDivElement>
}

export const GridCell = React.forwardRef<HTMLDivElement, GridCellProps>(
  (props, ref) => {
    const {
      height,
      showRightBorder,
      colIndex,
      rowId,
      column,
      disableDragEvents,
      getContext,
      onDragEnter,
      onDragOver,
      onClick,
      onKeyDown,
      onMouseDown,
      onMouseUp,
      onDoubleClick,
      onKeyUp,
      onMouseOver,
      className,
      hasFocus,
      tabIndex,
      editCellState,
      ...other
    } = props
    const ctx = getContext()
    const apiRef = useGridApiContext()
    const cellRef = React.useRef<HTMLDivElement>(null)
    const handleRef = useForkRef(ref, cellRef)
    const focusElementRef = React.useRef<FocusElement>(null)
    const rootProps = useGridRootProps()
    const width = column.getSize()
    const align = column.columnDef.meta?.align
    const field = column.id
    const isEditable = apiRef.current.isCellEditable({ rowId, field })
    const cellMode = apiRef.current.getCellMode(rowId, field)
    const hasError = editCellState?.error ?? false

    const style = {
      minWidth: width,
      maxWidth: width,
      minHeight: height,
      maxHeight: height === 'auto' ? 'none' : height, // max-height doesn't support "auto"
    }

    const handleFocus: any = other.onFocus

    const publishMouseUp = React.useCallback(
      (eventName: GridEvents) => (event: React.MouseEvent<HTMLDivElement>) => {
        const params = { field, rowId: rowId }
        apiRef.current.publishEvent(eventName as any, params as any, event)

        if (onMouseUp) {
          onMouseUp(event)
        }
      },
      [apiRef, field, onMouseUp, rowId]
    )

    const publishMouseDown = React.useCallback(
      (eventName: GridEvents) => (event: React.MouseEvent<HTMLDivElement>) => {
        const params = { field, rowId: rowId }
        if (
          apiRef.current.tableInstance.getColumn(field)?.columnDef.meta
            ?.type === 'actions'
        ) {
          return
        }

        apiRef.current.publishEvent(eventName as any, params as any, event)

        if (onMouseDown) {
          onMouseDown(event)
        }
      },
      [apiRef, field, onMouseDown, rowId]
    )

    const publish = React.useCallback(
      (eventName: keyof GridCellEventLookup, propHandler: any) =>
        (event: React.SyntheticEvent<HTMLDivElement>) => {
          const isEditable = apiRef.current.isCellEditable({ rowId, field })
          const cellMode = apiRef.current.getCellMode(rowId, field)
          // Ignore portal
          if (!event.currentTarget.contains(event.target as Element)) {
            return
          }
          if (
            eventName === 'cellClick' &&
            apiRef.current.tableInstance.getColumn(field)?.columnDef.meta
              ?.type === 'actions'
          ) {
            return
          }

          apiRef.current.publishEvent(
            eventName,
            { field, rowId, isEditable, cellMode },
            event as any
          )

          if (propHandler) {
            propHandler(event)
          }
        },
      [apiRef, field, rowId]
    )

    const draggableEventHandlers = disableDragEvents
      ? null
      : {
          onDragEnter: publish('cellDragEnter', onDragEnter),
          onDragOver: publish('cellDragOver', onDragOver),
        }

    React.useEffect(() => {
      if (!hasFocus || cellMode === GridCellModes.Edit) {
        return
      }
      const doc = ownerDocument(apiRef.current.rootElementRef!.current)!
      if (cellRef.current && !cellRef.current.contains(doc.activeElement!)) {
        const focusableElement =
          cellRef.current!.querySelector<HTMLElement>('[tabindex="0"]')
        const elementToFocus =
          focusElementRef.current || focusableElement || cellRef.current

        if (doesSupportPreventScroll()) {
          elementToFocus.focus({ preventScroll: true })
        } else {
          const scrollPosition = apiRef.current.getScrollPosition()
          elementToFocus.focus()
          apiRef.current.scroll(scrollPosition)
        }
      }
    }, [hasFocus, cellMode, apiRef])

    let content: React.ReactNode = null

    if (editCellState == null && column.columnDef.cell) {
      content = flexRender(column.columnDef.cell, {
        ...ctx,
        focusElementRef,
        hasFocus,
        tabIndex,
        onAction: rootProps.onAction,
        apiRef: apiRef.current,
      })
    }
    if (editCellState != null && column.columnDef?.meta?.renderEditCell) {
      content = flexRender(column.columnDef.meta.renderEditCell as any, {
        ...ctx,
        ...editCellState,
        hasFocus,
        tabIndex,
        onAction: rootProps.onAction,
        apiRef: apiRef.current,
      })
    }

    const tabIndexOverride = cellMode === 'view' || !isEditable ? tabIndex : -1

    return (
      <div
        ref={handleRef}
        style={style}
        className={clsx(
          styles.cell,
          rootProps.classes?.cell,
          showRightBorder && [styles.withBorder, rootProps.classes?.withBorder],
          isEditable && [
            styles['cell--editable'],
            rootProps.classes?.['cell--editable'],
          ],
          hasError && styles['cellError'],
          // @ts-ignore
          styles[`cell--text${capitalize(align)}`],
          // @ts-ignore
          rootProps.classes?.[`cell--text${capitalize(align)}`],
          className
        )}
        role="cell"
        data-field={field}
        data-colindex={colIndex}
        aria-colindex={colIndex + 1}
        tabIndex={tabIndexOverride}
        onDoubleClick={publish('cellDoubleClick', onDoubleClick)}
        onMouseDown={publishMouseDown('cellMouseDown')}
        onMouseOver={publish('cellMouseOver', onMouseOver)}
        onMouseUp={publishMouseUp('cellMouseUp')}
        onClick={publish('cellClick', onClick)}
        onKeyDown={publish('cellKeyDown', onKeyDown)}
        onKeyUp={publish('cellKeyUp', onKeyUp)}
        {...draggableEventHandlers}
        onFocus={handleFocus}
      >
        {content}
      </div>
    )
  }
)

GridCell.displayName = 'GridCell'
